import { ChangeDetectorRef, Component, QueryList, ViewChild, ViewChildren, ViewEncapsulation, OnInit } from '@angular/core';
import * as lo_ from 'lodash';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { ScrollablePopoverDirective } from '../../../directives/scrollable-popover/scrollable-popover.directive';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DataArea } from '../../../models/data-area';
import { DrObject } from '../../../models/dr-object';
import { Endpoint, HelpIndex, MessageType, ReviewSystem, UpdateStatusType } from '../../../models/enums';
import { ExportStream } from '../../../models/export-stream';
import { EmailOutputFormat, ExportInputName, ExportStreamOptions, TiffMode } from '../../../models/export-stream-options';
import { ExportVolume } from '../../../models/export-volume';
import { Timezone } from '../../../models/timezone';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { TimezoneModalComponent } from '../timezone-preferences/timezone-modal.component';
import { ExportComponent } from '../../export/export.component';

@Component({
    standalone: false,
    selector: 'res-update-export-volume',
    templateUrl: './update-export-volume-modal.component.html',
    styleUrls: ['./update-export-volume-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UpdateExportVolumeModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(ExportComponent) exportComponent: ExportComponent;
    @ViewChildren(ScrollablePopoverDirective) errorPopovers: QueryList<ScrollablePopoverDirective>;

    automationId = 'update-export-volume';
    modalClass = 'update-export-volume-modal';
    modalHelpIndex = HelpIndex.exports_update_export_vol_dlg;
    isBusy = false;
    isBusyMessage: string;
    updateOption = 'retryProductionErrorsOnly';
    exportStream: ExportStream;
    exportVolume: ExportVolume;
    exportOptions: ExportStreamOptions;
    enableSearchTermQueries: boolean;
    isReviewProject: boolean;

    extraOptions: {
        isBusy: Array<Endpoint>,
        exportDataAreas: Array<DataArea>,
        timezones: Array<Timezone>,
        allTimezones: Array<Timezone>,
        prevSelectedTimezone: string
    };

    onChange = {
        updateOption: (option: string): void => {
            this.updateOption = option;
            this.setOptions();
            if (this.updateOption === 'redoEntireProduction' || this.updateOption === 'addOutputFormats') {
                this.enableSearchTermQueries = this.updateOption === 'addOutputFormats' && this.modalData.exportVolume.options?.includePdfConversion ? true : false;
                this.exportComponent?.bulkSearchComponent?.init(this.exportOptions.custodianReportQueries, this.enableSearchTermQueries);
            }
        },
        selectedTimezone: (): void => {
            if (this.exportOptions.timezone === 'Other') {
                this.commonService.openModal(TimezoneModalComponent, { modalData: this.extraOptions.allTimezones }).then((value) => {
                    if (value.modalResult && value.modalData instanceof Timezone) {
                        this.extraOptions.timezones.splice(-1, 1);
                        this.extraOptions.timezones.push(value.modalData);

                        this.extraOptions.timezones.push(new Timezone({
                            description: 'Other...',
                            id: 'Other'
                        }));

                        this.exportOptions.timezone = value.modalData.id;
                        this.extraOptions.prevSelectedTimezone = this.exportOptions.timezone;
                    } else {
                        this.exportOptions.timezone = this.extraOptions.prevSelectedTimezone;
                    }
                });
            } else {
                this.extraOptions.prevSelectedTimezone = this.exportOptions.timezone;
            }
        },
        productionSettingType: (optionName: string): void => {
            switch (optionName) {
                case 'Native': {
                    // exportOptions.includeNativeFiles
                    this.exportOptions.includeNativeFiles = !this.exportOptions.includeNativeFiles;
                    this.validation.directory.nativeSubdirectoryInput.name = '';
                    this.validation.directory.nativeSubdirectoryInput.invalid = false;
                    this.exportOptions.emailOutputFormat = this.exportOptions.includeNativeFiles && this.exportOptions.includeSeperateEmailAttachments ? EmailOutputFormat.HTML_MHTML_FORMAT : null;
                    break;
                }
                case 'Extracted Text': {
                    // exportOptions.includeTextConversion
                    this.exportOptions.includeTextConversion = !this.exportOptions.includeTextConversion;
                    this.validation.directory.textSubdirectoryInput.name = '';
                    this.validation.directory.textSubdirectoryInput.invalid = false;
                    this.exportOptions.excludeEmailHeaders = false;
                    break;
                }
                case 'PDF': {
                    // exportOptions.includePdfConversion
                    this.exportOptions.includePdfConversion = !this.exportOptions.includePdfConversion;
                    this.validation.directory.pdfSubdirectoryInput.name = '';
                    this.validation.directory.pdfSubdirectoryInput.invalid = false;
                    if (!this.exportOptions.includeTiffConversion) {
                        if (!this.exportOptions.includePdfConversion) {
                            this.exportOptions.tiffBW = null;
                            this.exportOptions.convertImagesToPdf = false;
                        } else {
                            this.exportOptions.tiffBW = true;
                        }
                    }
                    break;
                }
                case 'TIFF': {
                    // exportOptions.includeTiffConversion
                    this.exportOptions.includeTiffConversion = !this.exportOptions.includeTiffConversion;
                    this.validation.directory.tiffSubdirectoryInput.name = '';
                    this.validation.directory.tiffSubdirectoryInput.invalid = false;
                    this.exportOptions.tiffMode = this.exportOptions.includeTiffConversion ? TiffMode.SINGLE_PAGE : null;

                    if (!this.exportOptions.includePdfConversion) {
                        if (!this.exportOptions.includeTiffConversion) {
                            this.exportOptions.tiffBW = null;
                            this.exportOptions.convertImagesToPdf = false;
                        } else {
                            this.exportOptions.tiffBW = true;
                        }
                    }
                    break;
                }
            }
        }
    };

    disabled = {
        includeNativeFiles: false,
        includeTextConversion: false,
        includePdfConversion: false,
        includeTiffConversion: false,
        tiffBW: (): boolean => {
            return (!this.exportOptions.includePdfConversion && !this.exportOptions.includeTiffConversion) ||
                (this.updateOption === 'addOutputFormats' && this.exportOptions.includePdfConversion === this.exportVolume.options.includePdfConversion &&
                    this.exportOptions.includeTiffConversion === this.exportVolume.options.includeTiffConversion);
        }
    };

    validation = {
        drObject: new DrObject(),
        dataAreaHandle: {
            check: (): void => {
                this.validation.dataAreaHandle.invalid = !(this.exportOptions.dataAreaHandle);
            },
            invalid: false,
            errorMessage: `Please select an Export Data Area.`
        },
        directory: {
            nativeSubdirectoryInput: new ExportInputName(this.changeDetector, { errorType: 'error', maxChar: 50 }),
            textSubdirectoryInput: new ExportInputName(this.changeDetector, { errorType: 'error', maxChar: 50 }),
            pdfSubdirectoryInput: new ExportInputName(this.changeDetector, { errorType: 'error', maxChar: 50 }),
            tiffSubdirectoryInput: new ExportInputName(this.changeDetector, { errorType: 'error', maxChar: 50 }),
        },
        valid: (): boolean => {
            let invalid = false;
            this.validation.dataAreaHandle.check();
            invalid = invalid || this.validation.dataAreaHandle.invalid;
            this.validation.directory.nativeSubdirectoryInput.check();
            invalid = invalid || this.validation.directory.nativeSubdirectoryInput.invalid;
            this.validation.directory.textSubdirectoryInput.check();
            invalid = invalid || this.validation.directory.textSubdirectoryInput.invalid;
            this.validation.directory.pdfSubdirectoryInput.check();
            invalid = invalid || this.validation.directory.pdfSubdirectoryInput.invalid;
            this.validation.directory.tiffSubdirectoryInput.check();
            invalid = invalid || this.validation.directory.tiffSubdirectoryInput.invalid;
            this.changeDetector.detectChanges();
            if (invalid) {
                if (this.errorPopovers) {
                    let visiblePopovers = this.errorPopovers.toArray()
                        .concat(this.exportComponent.errorPopovers.toArray())
                        .filter(popover => popover.resScrollablePopover);
                    if (visiblePopovers && visiblePopovers.length > 0) {
                        visiblePopovers = visiblePopovers.sort((a, b) => {
                            const rectA = a.inputElement.getBoundingClientRect();
                            const rectB = b.inputElement.getBoundingClientRect();
                            if (rectA.top > rectB.top) { return 1; }
                            if (rectA.top < rectB.top) { return -1; }
                            if (rectA.top === rectB.top) { return 0; }
                        });
                        visiblePopovers[0].focusOnInput();
                    }
                }
            }
            return !invalid;
        }
    };

    constructor(
        private modalService: BsModalService,
        public deleteEntityRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService,
        private changeDetector: ChangeDetectorRef) {
        super(modalService, deleteEntityRef);
        this.extraOptions = {
            isBusy: new Array<Endpoint>(),
            exportDataAreas: new Array<DataArea>(),
            timezones: new Array<Timezone>(),
            allTimezones: new Array<Timezone>(),
            prevSelectedTimezone: null
        };
        this.exportOptions = new ExportStreamOptions();
        if (this.modalData && this.modalData.exportStream instanceof ExportStream && this.modalData.exportVolume instanceof ExportVolume) {
            this.exportStream = this.modalData.exportStream as ExportStream;
            this.exportVolume = this.modalData.exportVolume as ExportVolume;
            this.setOptions(true);
        }
    }

    ngOnInit(): void {
        this.loadExportDataAreas();
        this.loadTimezones();
        this.isReviewProject = this.commonService.currentProject?.reviewSystem !== ReviewSystem.NONE;
    }

    loadExportDataAreas(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading...';
        this.extraOptions.isBusy.push(Endpoint.orgManager_listExportDataAreas);
        this.restService.post(Endpoint.orgManager_listExportDataAreas, {}).then(response => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.orgManager_listExportDataAreas), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
            if (response && response.exportDataAreas) {
                (response.exportDataAreas as Array<any>).map(responseItem => new DataArea(responseItem)).forEach((item) => {
                    this.extraOptions.exportDataAreas.push(item);
                });
            }
        }, error => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.orgManager_listExportDataAreas), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
        });
    }

    loadTimezones(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading...';

        this.extraOptions.isBusy.push(Endpoint.realmManager_listDRTimezones);
        this.restService.post(Endpoint.realmManager_listDRTimezones, {}).then(response => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.realmManager_listDRTimezones), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
            if (response && response.timezones) {
                (response.timezones as Array<any>).map(responseItem => new Timezone(responseItem)).forEach((item) => {
                    if (this.extraOptions.timezones.length > 0 && this.extraOptions.timezones[0].id === item.id) {
                        this.extraOptions.timezones.splice(0, 1);
                    }
                    this.extraOptions.timezones.push(item);
                    if (!this.exportOptions.timezone && item.id === 'UTC') {
                        this.exportOptions.timezone = item.id;
                    }
                    if (item.id === 'UTC') {
                        this.extraOptions.prevSelectedTimezone = item.id;
                    }
                });
            }
            const spacingOffsets = [-4.5, 3.5, 12.5];
            spacingOffsets.forEach(spacingOffset => {
                this.extraOptions.timezones.push(new Timezone({ description: '', id: '', offsetInfo: { hours: spacingOffset, mins: 0, negative: false } }));
            });

            this.extraOptions.timezones = this.extraOptions.timezones.sort((t1, t2): number => {
                const offset1 = t1.offsetInfo.negative ? -1 * t1.offsetInfo.hours : t1.offsetInfo.hours;
                const offset2 = t2.offsetInfo.negative ? -1 * t2.offsetInfo.hours : t2.offsetInfo.hours;
                if (offset1 > offset2) {
                    return 1;
                } else if (offset1 < offset2) {
                    return -1;
                }
                return 0;
            });

            this.extraOptions.timezones.push(new Timezone({
                description: 'Other...',
                id: 'Other'
            }));
        }, error => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.realmManager_listDRTimezones), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
        });

        this.extraOptions.isBusy.push(Endpoint.realmManager_listAllTimezones);
        this.restService.post(Endpoint.realmManager_listAllTimezones, {}).then(response => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.realmManager_listAllTimezones), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
            if (response && response.timezones) {
                (response.timezones as Array<any>).map(responseItem => new Timezone(responseItem)).forEach((item) => {
                    this.extraOptions.allTimezones.push(item);
                });
            }
        }, error => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.realmManager_listAllTimezones), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
        });
    }

    setOptions(init = false): void {
        const dataAreaHandle = this.exportOptions ? this.exportOptions.dataAreaHandle : null;
        if (this.updateOption === 'addOutputFormats' || init) {
            this.exportOptions = lo_.cloneDeep(this.exportVolume.options);
        }
        if (this.exportOptions) {
            if (!this.exportOptions.includeTiffConversion) {
                this.exportOptions.tiffMode = null;
                if (!this.exportOptions.includePdfConversion) {
                    this.exportOptions.tiffBW = null;
                }
            }
            if (dataAreaHandle) {
                this.exportOptions.dataAreaHandle = dataAreaHandle;
            }
            this.disabled.includeNativeFiles = this.exportOptions.includeNativeFiles && this.updateOption === 'addOutputFormats';
            this.disabled.includeTextConversion = this.exportOptions.includeTextConversion && this.updateOption === 'addOutputFormats';
            this.disabled.includePdfConversion = this.exportOptions.includePdfConversion && this.updateOption === 'addOutputFormats';
            this.disabled.includeTiffConversion = this.exportOptions.includeTiffConversion && this.updateOption === 'addOutputFormats';
        } else {
            this.exportOptions = this.exportStream ? this.exportStream.options : new ExportStreamOptions();
        }
    }

    okEnabled(): boolean {
        return Boolean(!this.isBusy && this.exportVolume && this.exportStream);
    }

    ok(): void {
        const exportOptions = this.exportComponent?.getExportStreamOptions();
        if (this.validation.valid()) {
            const dataAreaName = this.extraOptions.exportDataAreas.find(eda => eda.handle === this.exportOptions.dataAreaHandle).name;
            if (this.validation.directory.nativeSubdirectoryInput.name) {
                this.exportOptions.nativeSubdirectoryName = this.validation.directory.nativeSubdirectoryInput.name;
            }
            if (!this.exportOptions.nativeSubdirectoryName) {
                this.exportOptions.nativeSubdirectoryName = null;
            }
            if (this.validation.directory.textSubdirectoryInput.name) {
                this.exportOptions.textSubdirectoryName = this.validation.directory.textSubdirectoryInput.name;
            }
            if (!this.exportOptions.textSubdirectoryName) {
                this.exportOptions.textSubdirectoryName = null;
            }
            if (this.validation.directory.pdfSubdirectoryInput.name) {
                this.exportOptions.pdfSubdirectoryName = this.validation.directory.pdfSubdirectoryInput.name;
            }
            if (!this.exportOptions.pdfSubdirectoryName) {
                this.exportOptions.pdfSubdirectoryName = null;
            }
            if (this.validation.directory.tiffSubdirectoryInput.name) {
                this.exportOptions.tiffSubdirectoryName = this.validation.directory.tiffSubdirectoryInput.name;
            }
            if (!this.exportOptions.tiffSubdirectoryName) {
                this.exportOptions.tiffSubdirectoryName = null;
            }
            if (exportOptions) {
                this.exportOptions.custodianReportQueries = exportOptions.custodianReportQueries;
            }
            switch (this.updateOption) {
                case 'retryProductionErrorsOnly': {
                    this.updateExportVolume(Endpoint.groupViewManager_exportUpdateProduction, {
                        exportHandle: this.exportVolume.handle,
                        taskDescription: `Retrying Production Errors on ${this.exportStream.name} - ${this.exportVolume.name} in ${dataAreaName}`,
                        exportStreamOptions: this.exportOptions,
                        updateVolume: false,
                        retryErrors: true,
                        reproduceVolume: false
                    });
                    break;
                }
                case 'addOutputFormats': {
                    this.updateExportVolume(Endpoint.groupViewManager_exportUpdateProduction, {
                        exportHandle: this.exportVolume.handle,
                        taskDescription: `Producing Additional Formats on ${this.exportStream.name} - ${this.exportVolume.name} in ${dataAreaName}`,
                        exportStreamOptions: this.exportOptions,
                        updateVolume: true,
                        retryErrors: false,
                        reproduceVolume: false
                    });
                    break;
                }
                case 'redoEntireProduction': {
                    this.commonService.openConfirmationModal('This operation will delete any existing files at the selected target. Are you sure you want to continue?',
                        'Warning', 'OK', 'Cancel').then(result => {
                            if (result.modalResult) {
                                this.updateExportVolume(Endpoint.groupViewManager_exportUpdateProduction, {
                                    exportHandle: this.exportVolume.handle,
                                    taskDescription: `Re-exporting ${this.exportStream.name} - ${this.exportVolume.name} at ${dataAreaName}`,
                                    exportStreamOptions: this.exportOptions,
                                    updateVolume: false,
                                    retryErrors: false,
                                    reproduceVolume: true
                                });
                            }
                        });
                    break;
                }
            }
        }
    }

    updateExportVolume(endpoint: Endpoint, request: any): void {
        if (endpoint && request) {
            this.isBusyMessage = 'Updating export volume...';
            this.isBusy = true;
            this.restService.post(endpoint, request).then(response => {
                this.isBusy = false;
                if (response) {
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.EXPORT_VIEW);
                }
            }, () => {
                this.isBusy = false;
            });
            this.closeModal();
        }
    }
}
